import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { builder, BuilderComponent } from '@builder.io/react';
import lodash from 'lodash';
import axios from 'axios';
import pathfinder from '@fiverr-private/pathfinder';
import { mixpanel } from '@fiverr-private/obs';
import { raise, raiseToast } from '@fiverr-private/toast';
import { getContext } from '@fiverr-private/fiverr_context';
import { Center } from '@fiverr-private/layout_components';
import { Loader } from '@fiverr-private/feedback';
import {
    registerComponents,
    EditorAsyncPropsContextProvider,
    getEventContext,
    convertContextToMixpanel,
    sendEvent,
} from '@fiverr-private/builder_components';
import { PricingProvider } from '@fiverr-private/price_upfront_components';
import EventReportingManager from '../EventReportingManager';
import Logger from '../../../server/lib/logger';
import { getConvertPriceFromUSD, numberFormat, useBuilderTimeoutRetry } from './utils';

const BuilderRenderer = ({
    pageContent,
    builderModel,
    apiKey,
    contextData = {},
    logContext,
    reportingContext,
    graphqlResponse = {},
    dataModels = {},
    componentsAsyncProps = {},
    isBuilderTimeoutError = false,
    isPriceUpfrontEnabled = false,
    priceUpfrontConfiguration,
    priceUpfrontContext,
}) => {
    Logger.init(logContext);
    builder.init(apiKey);
    registerComponents(false, pageContent?.meta?.componentsUsed, Logger.getInstance().error);

    const { userId } = getContext();

    useEffect(() => {
        mixpanel.trackPageView({
            'Page Name': reportingContext?.page?.name,
            'Page Type': 'Content Page',
            ...(userId && { 'User Id': userId }),
            ...convertContextToMixpanel(pageContent?.data?.mixpanelPageViewEnrichment),
        });
    }, [reportingContext, userId, pageContent]);

    const builderComponentProps = {
        locale: contextData.locale,
        data: { contextData, graphqlResponse, dataModels },
        context: {
            lodash,
            axios,
            pathfinder,
            logger: Logger.getInstance(),
            numberFormat,
            convertPriceFromUSD: getConvertPriceFromUSD(contextData),
            raise,
            raiseToast,
            sendClickEvent: (eventName, componentName, eventEnrichment = {}) => {
                const context = getEventContext({
                    componentName,
                    eventEnrichment,
                    eventName,
                });

                sendEvent(eventName, 'click', context, reportingContext);
            },
        },
        content: pageContent,
        model: builderModel,
        apiKey,
    };

    useBuilderTimeoutRetry(isBuilderTimeoutError);

    return (
        <EditorAsyncPropsContextProvider value={{ isEditMode: false, componentsAsyncProps }}>
            <PricingProvider
                isPriceUpfrontEnabled={isPriceUpfrontEnabled}
                priceUpfrontConfiguration={priceUpfrontConfiguration}
                priceUpfrontContext={priceUpfrontContext}
            >
                <EventReportingManager context={reportingContext}>
                    {!isBuilderTimeoutError ? (
                        <BuilderComponent {...builderComponentProps} />
                    ) : (
                        <Center paddingY="32">
                            <Loader size="lg" />
                        </Center>
                    )}
                </EventReportingManager>
            </PricingProvider>
        </EditorAsyncPropsContextProvider>
    );
};

BuilderRenderer.propTypes = {
    pageContent: PropTypes.object,
    contextData: PropTypes.object,
    reportingContext: PropTypes.object,
    logContext: PropTypes.object,
    builderModel: PropTypes.string.isRequired,
    apiKey: PropTypes.string.isRequired,
    graphqlResponse: PropTypes.string,
    dataModels: PropTypes.object,
    componentsAsyncProps: PropTypes.object,
    isBuilderTimeoutError: PropTypes.bool,
    isPriceUpfrontEnabled: PropTypes.bool,
    priceUpfrontConfiguration: PropTypes.object,
    priceUpfrontContext: PropTypes.object,
};

export default BuilderRenderer;
